import { ChangeDetectionStrategy, Component, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { SuperEjecutivoDescargaComponent } from '../super-ejecutivo-descarga/super-ejecutivo-descarga.component';
import { SuperEjecutivoDescargaService } from '../super-ejecutivo-descarga/super-ejecutivo-descarga.service';
import {
  descargarExcelSuperEjecutivo,
  descargarImagenTablaSuperEjecutivo,
  nombreArchivoTablaSuperEjecutivo,
} from '../super-ejecutivo-descarga/super-ejecutivo-descarga.util';
import { CLASE_FUERA_DE_IMAGEN_SUPER_EJECUTIVO, EXCEL_SUPER_EJECUTIVO, TEXTOS_DESCARGA_SUPER_EJECUTIVO } from '../super-ejecutivo.constants';
import { ITablaSuperEjecutivo } from '../super-ejecutivo.interface';
import { tablaExcelDeTablaSuperEjecutivo } from './super-ejecutivo-tabla.util';

/**
 * Una tabla de una respuesta o una propuesta del Super Ejecutivo, con su botón para descargarla como imagen o como
 * Excel. Las anchas se desplazan de lado sin ensanchar el panel.
 */
@Component({
  selector: 'app-super-ejecutivo-tabla',
  templateUrl: './super-ejecutivo-tabla.component.html',
  styleUrl: './super-ejecutivo-tabla.component.css',
  imports: [SuperEjecutivoDescargaComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperEjecutivoTablaComponent {
  public readonly tabla = input.required<ITablaSuperEjecutivo>();
  /** false mientras la respuesta sigue llegando: la tabla puede estar incompleta. */
  public readonly descargable = input(true);

  public readonly etiquetaDescarga = TEXTOS_DESCARGA_SUPER_EJECUTIVO.BOTON_TABLA;
  public readonly claseFueraDeImagen = CLASE_FUERA_DE_IMAGEN_SUPER_EJECUTIVO;
  /** Hay una descarga en curso: el botón se deshabilita para no armar el mismo archivo dos veces. */
  public readonly descargando = signal(false);

  private readonly descargas = inject(SuperEjecutivoDescargaService);
  private readonly tablaHtml = viewChild.required<ElementRef<HTMLTableElement>>('tablaHtml');

  /** Descarga la tabla como PNG, completa aunque en el panel se desplace de lado, con la fecha y la hora en el nombre. */
  public async descargarImagen(): Promise<void> {
    const tabla = this.tablaHtml().nativeElement;

    await this.descargar(() => descargarImagenTablaSuperEjecutivo(tabla, nombreArchivoTablaSuperEjecutivo(new Date(), 'png')));
  }

  /** Descarga la tabla en Excel, con las cifras como números, con la fecha y la hora en el nombre. */
  public async descargarExcel(): Promise<void> {
    await this.descargar(() =>
      descargarExcelSuperEjecutivo(
        tablaExcelDeTablaSuperEjecutivo(this.tabla()),
        nombreArchivoTablaSuperEjecutivo(new Date(), 'xlsx'),
        EXCEL_SUPER_EJECUTIVO.hojaTabla,
      ),
    );
  }

  private async descargar(armarArchivo: () => Promise<void>): Promise<void> {
    this.descargando.set(true);

    try {
      await this.descargas.descargar(armarArchivo);
    } finally {
      this.descargando.set(false);
    }
  }
}
